import axios from "axios";
import {
  LOGIN_URL,
  REGISTER_URL,
  FORGOT_PASSWORD_URL,
  GET_CAPTCHA_URL,
  SEND_CAPTCHA_URL,
  RE_CAPTCHA_URL,
  VERIFY_CAPTCHA_URL,
  CHANGE_PASSWORD_URL,
} from "../constants";

/**
 * Logs a user in with the provided credentials
 * @function login
 * @param {string} usernameOrEmail The username or email to login with
 * @param {string} password A password to log in with
 * @param {string} authToken A token to be used instead of a username/email or password
 * @returns {object} The user object
 */
export const login = async (usernameOrEmail, password, authToken) => {
  try {
    const request =
      usernameOrEmail && password
        ? { data: { usernameOrEmail, password } }
        : {
            data: { usernameOrEmail, password },
            headers: { authorization: authToken },
          };
    const response = await axios(LOGIN_URL, {
      method: "POST",
      ...request,
    });
    return response.data;
  } catch (err) {
    throw new Error(err.response.data.error);
  }
};

/**
 * Registers a user with the provided credentials
 * @param {string} email A user's email address
 * @param {string} firstName A user's first name
 * * @param {string} lastName A user's last name
 * @param {string} username A user's username
 * @param {string} password A user's password
 * @param {string} userType A user's user type
 * @returns {object} The user object
 */
export const registerUser = async (
  email,
  firstName,
  lastName,
  username,
  password,
  userType,
  dateOfBirth
) => {
  try {
    const response = await axios.post(REGISTER_URL, {
      email,
      firstName,
      lastName,
      username,
      password,
      userType,
      dateOfBirth,
    });
    return response;
  } catch (err) {
    throw new Error(err.response.data.error);
  }
};

export const forgotPassword = async (email, newPassword, captcha) => {
  try {
    const response = await axios.patch(FORGOT_PASSWORD_URL, {
      email,
      newPassword,
      captcha,
    });
    return response;
  } catch (err) {
    throw new Error(err.response.data.error || "Failed to reset password");
  }
};

/**
 * Gets a captcha when first register
 * @function getCaptcha
 * @param {string} email The user's current email
 */

export const getCaptcha = async (email) => {
  try {
    const response = await axios.get(GET_CAPTCHA_URL, {
      params: {
        email,
      },
    });
  } catch (err) {
    throw new Error(err.response.data.error || "Failed to retrieve captcha");
  }
};

export const sendCaptcha = async (email) => {
  try {
    const response = await axios.get(SEND_CAPTCHA_URL, {
      params: {
        email,
      },
    });
    return response;
  } catch (err) {
    throw new Error(
      err.response
        ? err.response.data.error
        : "Failed to send verification code"
    );
  }
};

export const reCaptcha = async (email) => {
  try {
    const response = await axios.get(RE_CAPTCHA_URL, {
      params: {
        email,
      },
    });
    return response;
  } catch (err) {
    throw new Error(
      err.response
        ? err.response.data.error
        : "Failed to send verification code"
    );
  }
};

export const verifyCaptcha = async (email, captcha) => {
  try {
    const response = await axios.post(VERIFY_CAPTCHA_URL, {
      email,
      captcha,
    });
    return response;
  } catch (err) {
    throw new Error(
      err.response ? err.response.data.error : "Failed to verify email"
    );
  }
};

/**
 * Changes a users password
 * @function changePassword
 * @param {string} oldPassword The user's current password
 * @param {string} newPassword The new password
 * @param {string} authToken A user's auth token
 */
export const changePassword = async (oldPassword, newPassword, authToken) => {
  try {
    await axios.put(
      CHANGE_PASSWORD_URL,
      {
        oldPassword,
        newPassword,
      },
      {
        headers: {
          authorization: authToken,
        },
      }
    );
  } catch (err) {
    throw new Error(err.response.data.error);
  }
};
